import { useState, useCallback } from 'react';
import {
  Command,
  convertToolsToCommands,
  findMatchingCommands,
  getNextArgumentPlaceholderWithType,
} from '../helpers/commandAutocomplete';
import { Tool } from 'models/chatbot';

interface CommandAutocompleteHook {
  showCommandSuggestions: boolean;
  commandSuggestions: Command[];
  autocompleteSuggestion: string;
  handleInputChange: (value: string, tools: Tool[]) => void;
  handleTabPress: () => string | null;
  hideCommandSuggestions: () => void;
  selectCommand: (commandName: string, onCommandSelected?: () => void) => string;
}

const useCommandAutocomplete = (): CommandAutocompleteHook => {
  const [inputValue, setInputValue] = useState('');
  const [showCommandSuggestions, setShowCommandSuggestions] = useState(false);
  const [commandSuggestions, setCommandSuggestions] = useState<Command[]>([]);
  const [autocompleteSuggestion, setAutocompleteSuggestion] = useState('');
  const [commandSelected, setCommandSelected] = useState(false);

  const updateCommands = useCallback(
    (value: string, tools: Tool[]) => {
      const commands = convertToolsToCommands(tools);

      const reset = () => {
        setShowCommandSuggestions(false);
        setAutocompleteSuggestion('');
      };

      const parts = value.split(' ');
      const commandPart = parts[0];
      const commandName = commandPart.slice(1);
      const args = parts.slice(1).filter(Boolean);
      const endsWithSpace = value.endsWith(' ');
      const isSlash = value.startsWith('/');
      const typingCommandName = isSlash && parts.length === 1 && value !== '/';

      const mode = (() => {
        if (!isSlash) return 'plainText';

        if (!commandSelected && typingCommandName) return 'typingCommandName';

        if (isSlash && parts.length <= 1) return 'showCommandList';

        if (parts.length > 1 && endsWithSpace) return 'typingArgumentWithSpace';

        if (commandSelected && parts.length > 1) return 'typingArgument';

        return 'default';
      })();

      const handlers: Record<string, () => void> = {
        plainText: () => {
          reset();
          setCommandSelected(false);
        },

        showCommandList: () => {
          const suggestions = findMatchingCommands(commandPart, commands);
          setCommandSuggestions(suggestions);
          setShowCommandSuggestions(suggestions.length > 0);
          setAutocompleteSuggestion('');
        },

        typingCommandName: () => {
          const suggestions = findMatchingCommands(commandPart, commands);

          setCommandSuggestions(suggestions);
          setShowCommandSuggestions(suggestions.length > 0);

          if (!suggestions.length) {
            return reset();
          }

          const match = suggestions[0].name;
          const suggestion = `/${match}`;

          setAutocompleteSuggestion(suggestion.length > value.length ? suggestion : '');
        },

        typingArgument: () => {
          reset();
        },

        typingArgumentWithSpace: () => {
          const command = commands.find((c) => c.name === commandName);
          if (!command?.arguments) return reset();

          const next = getNextArgumentPlaceholderWithType(commandName, args, commands);
          if (!next) return reset();

          const base = `/${commandName}`;
          const argsString = args.length ? ` ${args.join(' ')}` : '';

          setAutocompleteSuggestion(`${base}${argsString} ${next}`);
        },

        default: () => reset(),
      };

      handlers[mode]();
    },
    [commandSelected],
  );

  const handleInputChange = useCallback(
    (value: string, tools: Tool[]) => {
      setInputValue(value);
      updateCommands(value, tools);
    },
    [updateCommands],
  );

  const handleTabPress = useCallback((): string | null => {
    // Only handle tab completion for command name (before any spaces)
    if (inputValue.startsWith('/') && !inputValue.includes(' ')) {
      if (autocompleteSuggestion && autocompleteSuggestion.length > inputValue.length) {
        // Complete the command name only
        const completedValue = autocompleteSuggestion;
        setInputValue(completedValue);
        setAutocompleteSuggestion('');
        return completedValue;
      }
    }

    return null;
  }, [inputValue, autocompleteSuggestion]);

  const hideCommandSuggestions = useCallback(() => {
    setShowCommandSuggestions(false);
  }, []);

  const selectCommand = useCallback((commandName: string, onCommandSelected?: () => void): string => {
    // Only insert the command name, not the full suggestion with placeholders
    const commandText = `/${commandName}`;
    setInputValue(commandText);
    setShowCommandSuggestions(false);
    // Mark that a command has been selected
    setCommandSelected(true);

    if (onCommandSelected) {
      onCommandSelected();
    }

    // Clear the autocomplete suggestion to prevent placeholders from appearing
    setAutocompleteSuggestion('');

    return commandText;
  }, []);

  return {
    showCommandSuggestions,
    commandSuggestions,
    autocompleteSuggestion,
    handleInputChange,
    handleTabPress,
    hideCommandSuggestions,
    selectCommand,
  };
};

export default useCommandAutocomplete;
